import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  useToast,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormHeader from '../../components/FormHeader/FormHeader.tsx';
import AleaStepper, { StepperStep } from '../../components/AleaStepper/AleaStepper.tsx';
import { useTranslation } from 'react-i18next';
import PersonalDataTab from './PersonalDataTab.tsx';
import FeatureActivationTab from './FeatureActivationTab.tsx';
import IncontinenceManagementTab from './IncontinenceManagementTab.tsx';
import BedsoresPreventionTab from './BedsoresPreventionTab.tsx';
import ResidentTabNavigation from '../../components/ResidentTabNavigation/ResidentTabNavigation.tsx';
import ResidentCreatedSuccessfully from './ResidentCreatedSuccessfully.tsx';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ResidentService from '../../services/ResidentService.ts';
import StationService from '../../services/StationService.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { ResidentFeature } from '../../constants/ResidentFeature.ts';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import { CareHomeDiaper } from '../../interfaces/Diaper/IDiaper.ts';
import DiaperService from '../../services/DiaperService.ts';
import { IIndividualPosition } from '../../interfaces';
import { ReposIntervalType } from '../../constants/ReposIntervalType.ts';
import {
  IRepoInterval,
  IResidentListItem,
} from '../../interfaces/Resident/IResidentListItem.ts';
import { IResidentReposInterval } from '../../interfaces/Resident/IResidentReposInterval.ts';
import { getLanguageKeyByType } from '../../utils/getLanguageKeyByType.ts';
import generateGenderPayload from '../../utils/generateGenderPayload.ts';
import AleaConfirmModal from '../../modals/AleaConfirmModal/AleaConfirmModal.tsx';
import { ICreateResidentDiaperResponse } from '../../interfaces/Diaper/ICreateResidentDiaperResponse.ts';
import { ResidentState } from '../../interfaces/Resident/IResidentUpdatePayload.ts';
import CareHomeService from '../../services/CareHomeService.ts';
import { ICareHome } from '../../interfaces/CareHome/ICareHome.ts';
import { FORCE_URINE_DETECTION_ACTIVE } from '../../constants/featureActivationFlags.ts';
import { hasFeaturePermission } from '../../utils/featurePermission.ts';
import bodyRegions from '../../components/Resident/BedsoreSelection/region_mapping.json';

interface PersonalDataFormValues {
  firstName: string;
  lastName: string;
  weight: string;
  height: string;
  livingArea: string;
  room: string;
  gender: 'Woman' | 'Man' | 'Diverse' | null;
}

interface FeatureActivationFormValues {
  incontinenceManagementUrine: boolean | null;
  safeZone: boolean | null;
  bedsoresPrevention: boolean | null;
  fallDetection: boolean | null;
}

interface IncontinenceType {
  type: string;
  day: number;
  night: number;
}

interface IncontinenceManagementFormValues {
  incontinenceTypes: IncontinenceType[];
  has_catheter?: boolean;
}

interface BedsoresPreventionFormValues {
  selections: {
    [key: string]: {
      risk: string;
      note: string;
    };
  };
  positions: IIndividualPosition[];
  dayTime: number;
  nightTime: number;
  enableReposIntervals: boolean;
  enableReposPositionIntervals: boolean;
  enableReposBedsores: boolean;
  bedsoreOps?: Array<{ action: 'create' | 'update' | 'delete'; key: string; risk?: string; note?: string }>; // queued ops for submit
}

const NewResidentPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(() => {
    const queryParams = new URLSearchParams(location.search);
    return parseInt(queryParams.get('tab') || '0', 10);
  });
  const formRef = useRef<HTMLDivElement>(null);

  const [stations, setStations] = useState<{ id: string; name: string }[]>([]);
  const [diapers, setDiapers] = useState<CareHomeDiaper[]>([]);
  const [diaperUsage, setDiaperUsage] = useState<
    ICreateResidentDiaperResponse[]
  >([]);
  const [reposIntervals, setReposIntervals] = useState<IRepoInterval[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState({
    stations: false,
    diapers: false,
    reposIntervals: false,
  });

  const { user } = useAuth();
  const [isResidentCreated, setIsResidentCreated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentResidentData, setCurrentResidentData] =
    useState<IResidentListItem | null>(null);
  const [createdResidentId, setCreatedResidentId] = useState<string | null>(null);
  const [createdAtStepIndex, setCreatedAtStepIndex] = useState<number | null>(null);

  const [initialPositions, setInitialPositions] = useState<
    IIndividualPosition[]
  >([]);

  const [showIncontinenceValidation, setShowIncontinenceValidation] =
    useState(false);
  const [careHome, setCareHome] = useState<ICareHome | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const pendingNavigateRef = useRef<string | null>(null);

  // Listen to global leave attempts from layout (logo, any link)
  useEffect(() => {
    const handler = (e: Event) => {
      if (id) return; // only for create flow
      const to = (e as CustomEvent<string>).detail;
      if (!to) return;
      // If resident is already created (success screen shown), allow navigation without modal
      if (isResidentCreated) {
        navigate(to);
        return;
      }
      // Otherwise show confirm modal
      setShowLeaveConfirm(true);
      pendingNavigateRef.current = to;
    };
    window.addEventListener('attempt-leave-from-add', handler as EventListener);
    return () => window.removeEventListener('attempt-leave-from-add', handler as EventListener);
  }, [id, isResidentCreated, navigate]);

  // Time interval related state for bedsores prevention
  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([
    { start: '00:00', end: '24:00', id: 'manual-1', isExisting: false, dbId: null, intervalValue: '03:00:00' }
  ]);
  const [originalIntervals, setOriginalIntervals] = useState<TimeInterval[]>([]);

  // Type for time interval
  interface TimeInterval {
    start: string;
    end: string;
    id: string;
    isExisting: boolean;
    dbId: string | null;
    intervalValue: string; // Individual interval duration for each interval
  }



  /**
   * Fetches repositioning intervals data and updates the time intervals state
   */
  const fetchReposIntervals = async (residentId: string) => {
    try {
      const response =
        await ResidentService.listResidentReposIntervals(residentId);

      console.log('📋 API Response:', response);
      console.log('📋 Response items:', response.items);

      // Default positions
      const defaultPositions: IIndividualPosition[] = [
        { name: 'Supine', maxHours: 0 },
        { name: 'Right Side', maxHours: 0 },
        { name: 'Left Side', maxHours: 0 },
        { name: 'Prone', maxHours: 0 },
        { name: 'Sitting', maxHours: 0 },
        { name: 'Wheelchair', maxHours: 0 },
      ];

      const fetchedPositions: IIndividualPosition[] = [];
      let fetchedDayTime = 3;
      let fetchedNightTime = 3;
      const fetchedGeneralDayIntervals: TimeInterval[] = [];

      if (response.items.length > 0) {
        response.items.forEach((interval: IResidentReposInterval) => {
          const hours = parseInt(interval.value.split(':')[0], 10);
          console.log('🔍 Processing interval:', interval);
          console.log('🔍 Interval type:', interval.type);
          console.log('🔍 Interval start:', interval.start);
          console.log('🔍 Interval end:', interval.end);

          if (interval.type === 'General Day') {
            fetchedDayTime = hours;
            // Extract time from start and end (format: HH:MM:SS or HH:MM)
            let startTime = '08:00';
            let endTime = '24:00';

            if (interval.start) {
              // Handle various time formats: "HH:MM:SS", "HH:MM", "T12:00:00", etc.
              let timeString = interval.start;

              // If it's a full datetime string with T
              if (timeString.includes('T')) {
                timeString = timeString.split('T')[1];
              }

              // Extract HH:MM from the time string
              const timeMatch = timeString.match(/(\d{1,2}):(\d{2})/);
              if (timeMatch) {
                startTime = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
              }
            }

            if (interval.end) {
              // Handle various time formats: "HH:MM:SS", "HH:MM", "T12:00:00", etc.
              let timeString = interval.end;

              // If it's a full datetime string with T
              if (timeString.includes('T')) {
                timeString = timeString.split('T')[1];
              }

              // Extract HH:MM from the time string
              const timeMatch = timeString.match(/(\d{1,2}):(\d{2})/);
              if (timeMatch) {
                endTime = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;

                // Convert 23:59 back to 24:00 for UI display
                if (endTime === '23:59') {
                  endTime = '24:00';
                }
              }
            }

            console.log('✅ Adding General Day interval:', {
              start: startTime,
              end: endTime,
              id: interval.id,
              isExisting: true,
              dbId: interval.id,
              intervalValue: interval.value
            });

            if (endTime === '24:00') {
              console.log('🔄 Converted 23:59 → 24:00 for UI display');
            }

            fetchedGeneralDayIntervals.push({
              start: startTime,
              end: endTime,
              id: `existing-${interval.id}`,
              isExisting: true,
              dbId: interval.id,
              intervalValue: interval.value // Store individual interval value
            });
          } else if (interval.type === 'General Night') {
            fetchedNightTime = hours;
          } else {
            fetchedPositions.push({
              name: interval.type,
              maxHours: hours,
            });
          }
        });
      }

      // Sort intervals by start time (earliest first) to maintain correct order
      console.log('🔀 Sorting intervals by start time...');
      fetchedGeneralDayIntervals.sort((a, b) => {
        const timeA = a.start.replace(':', '');
        const timeB = b.start.replace(':', '');
        const result = parseInt(timeA) - parseInt(timeB);
        console.log(`  Comparing ${a.start} vs ${b.start}: ${result}`);
        return result;
      });

      console.log('📊 Final sorted fetchedGeneralDayIntervals:', fetchedGeneralDayIntervals.map(interval => `${interval.start}-${interval.end}`));

      // If no General Day intervals found, use default
      if (fetchedGeneralDayIntervals.length === 0) {
        console.log('⚠️ No General Day intervals found, using default: 00:00-24:00');
        fetchedGeneralDayIntervals.push(
          { start: '00:00', end: '24:00', id: 'manual-1', isExisting: false, dbId: null, intervalValue: '03:00:00' }
        );
      }

      // Combine fetched positions with default positions
      const combinedPositions = defaultPositions.map((defaultPos) => {
        const existingPos = fetchedPositions.find(
          (pos) => pos.name === defaultPos.name
        );
        return existingPos || defaultPos;
      });

      bedsoresPreventionMethods.setValue('dayTime', fetchedDayTime);
      bedsoresPreventionMethods.setValue('nightTime', fetchedNightTime);
      bedsoresPreventionMethods.setValue('positions', combinedPositions);

      // Set time intervals and store original for comparison
      console.log('🎯 Setting time intervals:', fetchedGeneralDayIntervals);
      setTimeIntervals(fetchedGeneralDayIntervals);
      setOriginalIntervals([...fetchedGeneralDayIntervals]);

      // set initialPositions
      setInitialPositions(combinedPositions);
    } catch (error) {
      console.log('❌ Error fetching repos intervals:', error);
    }
  };

  const personalDataSchema = yup.object().shape({
    firstName: yup
      .string()
      .required(t('requiredField', { field: t('firstName') })),
    lastName: yup
      .string()
      .required(t('requiredField', { field: t('lastName') })),
    weight: yup.string(),
    height: yup.string(),
    livingArea: yup
      .string()
      .required(t('requiredField', { field: t('livingArea') })),
    room: yup.string().required(t('requiredField', { field: t('room') })),
    gender: yup.string().required(t('requiredField', { field: t('gender') })),
  });

  const featureDecisionSchema = yup
    .boolean()
    .nullable()
    .required('Please decide for all features before continuing.');

  const featureActivationSchema = yup.object().shape({
    incontinenceManagementUrine: featureDecisionSchema,
    safeZone: featureDecisionSchema,
    bedsoresPrevention: featureDecisionSchema,
    fallDetection: featureDecisionSchema,
  });

  const incontinenceManagementSchema = yup.object().shape({
    incontinenceTypes: yup.array().of(
      yup.object().shape({
        type: yup.string(),
        day: yup.number().min(0).required(),
        night: yup.number().min(0).required(),
      })
    ),
    has_catheter: yup.boolean().notRequired(),
  });

  const bedsoresPreventionSchema = yup.object().shape({
    selections: yup.object().required(),
  });

  const personalDataMethods = useForm<PersonalDataFormValues>({
    resolver: yupResolver(personalDataSchema) as never,
    mode: 'onChange',
  });

  const featureActivationMethods = useForm<FeatureActivationFormValues>({
    resolver: yupResolver(featureActivationSchema) as never,
    mode: 'onChange',
    defaultValues: {
      incontinenceManagementUrine: FORCE_URINE_DETECTION_ACTIVE ? true : null,
      safeZone: null,
      bedsoresPrevention: null,
      fallDetection: null,
    },
  });

  const incontinenceManagementMethods =
    useForm<IncontinenceManagementFormValues>({
      resolver: yupResolver(incontinenceManagementSchema) as never,
      defaultValues: {
        incontinenceTypes: diapers.map((diaper) => ({
          type: diaper.diaper.name,
          day: 0,
          night: 0,
        })),
        has_catheter: false,
      },
      mode: 'onChange',
    });

  const bedsoresPreventionMethods = useForm<BedsoresPreventionFormValues>({
    resolver: yupResolver(bedsoresPreventionSchema) as never,
    mode: 'onChange',
    defaultValues: {
      selections: {},
      positions: [],
      enableReposIntervals: !id, // true for create mode, will be updated from service in edit mode
      enableReposPositionIntervals: false,
      enableReposBedsores: false,
      bedsoreOps: [],
    },
  });

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchStations(), fetchDiapers()]);
    };
    loadData();
    fetchCareHomeData();
  }, []);

  useEffect(() => {
    if (id && isDataLoaded.stations && isDataLoaded.diapers) {
      loadResidentData(id);
    } else if (!id) {
      setIsLoading(false);
    }

    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [id, isDataLoaded]);

  /**
   * Centralized function to load all resident data
   */
  const loadResidentData = async (residentId: string) => {
    try {
      console.log('🔄 Loading resident data for:', residentId);

      // Load resident data
      await fetchResidentData(residentId);

      // Load repos intervals
      await fetchReposIntervals(residentId);

      console.log('✅ All resident data loaded successfully');

      // Close loader after all data is loaded
      setIsLoading(false);
    } catch (error) {
      console.error('❌ Failed to load resident data:', error);
      setIsLoading(false);
    }
  };

  // Fetch repos intervals when resident ID changes
  useEffect(() => {
    if (id) {
      console.log('🔄 useEffect triggered with residentId:', id);
      // Note: fetchReposIntervals is now called within loadResidentData
    }
  }, [id]);

  const fetchResidentData = async (id: string) => {
    try {
      const residentData = await ResidentService.getResident(id);
      setCurrentResidentData(residentData);

      personalDataMethods.reset({
        firstName: residentData.first_name,
        lastName: residentData.last_name,
        weight: residentData.weight?.toString() || '',
        height: residentData.height?.toString() || '',
        livingArea: residentData.station?.id || '',
        room: residentData.room || '',
        gender: residentData.gender,
      });

      setDiaperUsage(residentData.diapers as ICreateResidentDiaperResponse[]);

      featureActivationMethods.reset({
        incontinenceManagementUrine: FORCE_URINE_DETECTION_ACTIVE
          ? true
          : residentData.features
            ? residentData.features.includes(
                ResidentFeature.INCONTINENCE_MANAGEMENT_URINE
              )
            : false,
        safeZone: residentData.features
          ? residentData.features.includes(ResidentFeature.SECURE_ZONE)
          : false,
        bedsoresPrevention: residentData.features
          ? residentData.features.includes(
              ResidentFeature.REPOSITIONING_MANAGEMENT
            )
          : false,
        fallDetection: residentData.features
          ? residentData.features.includes(ResidentFeature.FALL_MANAGEMENT)
          : false,
      });

      // Prefill catheter checkbox from resident data if available
      try {
        const currentIncont = incontinenceManagementMethods.getValues();
        incontinenceManagementMethods.reset({
          ...currentIncont,
          has_catheter: Boolean((residentData as any).has_catheter),
        });
      } catch (_) {}

      // Initialize diapers with false values
      const initialIncontinenceTypes = diapers.map((diaper) => ({
        type: diaper.diaper.name,
        day: 0,
        night: 0,
      }));

      // Update initialIncontinenceTypes based on residentData.diapers
      residentData.diapers.forEach((residentDiaper: any) => {
        const diaperIndex = initialIncontinenceTypes.findIndex(
          (item) => item.type === residentDiaper.diaper.name
        );
        if (diaperIndex !== -1) {
          if (residentDiaper.usage === 'Day') {
            initialIncontinenceTypes[diaperIndex].day =
              residentDiaper.usage_amount;
          } else if (residentDiaper.usage === 'Night') {
            initialIncontinenceTypes[diaperIndex].night =
              residentDiaper.usage_amount;
          }
        }
      });

      // Only update incontinenceTypes to avoid wiping other fields (like has_catheter)
      incontinenceManagementMethods.setValue('incontinenceTypes', initialIncontinenceTypes as any, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });

      bedsoresPreventionMethods.reset({
        selections: {},
        positions: [],
        enableReposIntervals: (residentData.feature_options || []).includes('Repositioning Intervals Enabled'),
        enableReposPositionIntervals: (residentData.feature_options || []).includes('Repositioning Position Intervals Enabled'),
        enableReposBedsores: (residentData.feature_options || []).includes('Repositioning Bedsores Enabled'),
      });

      setReposIntervals(residentData.repos_intervals);
    } catch (error) {
      console.error('Failed to fetch resident data:', error);
    }
  };

  /**
   * Fetches the list of stations for the dropdown.
   */
  const fetchStations = async () => {
    if (!user?.carehome?.id) {
      console.error('Care home ID is missing');
      return;
    }

    try {
      const response = await StationService.getListStations(
        1,
        100,
        '',
        user?.carehome?.id,
        'true'
      );
      setStations(response.items);
      setIsDataLoaded((prev) => ({ ...prev, stations: true }));
    } catch (error) {
      console.error('Failed to fetch stations', error);
    }
  };

  const fetchDiapers = async () => {
    try {
      const params: any = {
        page: 1,
        per_page: 50,
        is_active: true,
      };

      // Only add carehome_id if it exists
      if (user?.carehome?.id) {
        params.carehome_id = user.carehome.id;
      }

      const response = await DiaperService.getDiapers(params);
      setDiapers(response.items);
      setIsDataLoaded((prev) => ({ ...prev, diapers: true }));
    } catch (error) {
      console.error('Failed to fetch diapers', error);
      console.error('Error details:', error);
      toast({
        description: 'Failed to load incontinence products. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // CareHome data fetch
  const fetchCareHomeData = async () => {
    try {
      if (user?.carehome?.id) {
        const response = await CareHomeService.getCareHome(user.carehome.id);
        setCareHome(response);
      }
    } catch (error) {
      console.error('Error fetching care home data:', error);
    }
  };

  /**
   * Gets the station ID based on the station name.
   *
   * @param {string} stationName - The name of the station.
   * @returns {string | undefined} - The ID of the station.
   */
  const getStationIdByName = (stationName: string): string | undefined => {
    const station = stations.find((station) => station.name === stationName);
    return station?.id;
  };

  /**
   * Submit personal data to the server
   */
  const submitPersonalData = async (): Promise<string> => {
    const personalData = personalDataMethods.getValues();
    let stationId = getStationIdByName(personalData.livingArea);
    if (typeof stationId === 'undefined') {
      stationId = personalData.livingArea;
    }

    // feature_options - Bedsores Prevention switches
    const bpValues = bedsoresPreventionMethods.getValues();
    const feature_options: string[] = [];
    if (bpValues.enableReposIntervals) {
      feature_options.push('Repositioning Intervals Enabled');
    }
    if (bpValues.enableReposPositionIntervals) {
      feature_options.push('Repositioning Position Intervals Enabled');
    }
    if (bpValues.enableReposBedsores) {
      feature_options.push('Repositioning Bedsores Enabled');
    }

    if (id) {
      // Update existing Resident (edit mode)
      await ResidentService.updateResident(id, {
        ...currentResidentData,
        carehome_id: user?.carehome?.id || '',
        station_id: stationId || '',
        room: personalData.room,
        first_name: personalData.firstName,
        middle_name: null,
        last_name: personalData.lastName,
        weight: parseFloat(personalData.weight) || null,
        height: parseInt(personalData.height) || null,
        gender: generateGenderPayload(personalData.gender as string),
        date_of_birth: null,
        state: (currentResidentData?.state || 'Enabled') as ResidentState,
        is_active: true,
        has_catheter: Boolean(incontinenceManagementMethods.getValues().has_catheter),
        feature_options,
      });
      return id;
    } else if (isResidentCreated && createdResidentId) {
      // Update newly created Resident
      await ResidentService.updateResident(createdResidentId, {
        ...currentResidentData,
        carehome_id: user?.carehome?.id || '',
        station_id: stationId || '',
        room: personalData.room,
        first_name: personalData.firstName,
        middle_name: null,
        last_name: personalData.lastName,
        weight: parseFloat(personalData.weight) || null,
        height: parseInt(personalData.height) || null,
        gender: generateGenderPayload(personalData.gender as string),
        date_of_birth: null,
        state: (currentResidentData?.state || 'Enabled') as ResidentState,
        is_active: true,
        has_catheter: Boolean(incontinenceManagementMethods.getValues().has_catheter),
        feature_options,
      });
      return createdResidentId;
    } else {
      // Create new Resident
      const newResident = await ResidentService.createResident({
        carehome_id: user?.carehome?.id || '',
        station_id: stationId || '',
        room: personalData.room,
        first_name: personalData.firstName,
        middle_name: null,
        last_name: personalData.lastName,
        weight: parseFloat(personalData.weight) || null,
        height: parseInt(personalData.height) || null,
        gender: generateGenderPayload(personalData.gender as string),
        date_of_birth: null,
        state: 'Enabled',
        features: [],
        feature_options,
        is_active: true,
      });
      setIsResidentCreated(true);
      setCreatedResidentId(newResident.id);
      return newResident.id;
    }
  };

  /**
   * Submit feature activation to the server
   */
  const submitFeatureActivation = async (residentId: string) => {
    const featureActivation = featureActivationMethods.getValues();
    const selectedFeatures = [];
    if (featureActivation.incontinenceManagementUrine) {
      selectedFeatures.push(ResidentFeature.INCONTINENCE_MANAGEMENT_URINE);
    }
    if (featureActivation.safeZone) {
      selectedFeatures.push(ResidentFeature.SECURE_ZONE);
    }
    if (featureActivation.bedsoresPrevention) {
      selectedFeatures.push(ResidentFeature.REPOSITIONING_MANAGEMENT);
    }
    if (featureActivation.fallDetection) {
      selectedFeatures.push(ResidentFeature.FALL_MANAGEMENT);
    }

    await ResidentService.updateResidentFeatures(
      residentId,
      selectedFeatures
    );

    // Update currentResidentData with new features
    if (currentResidentData) {
      setCurrentResidentData({
        ...currentResidentData,
        features: selectedFeatures,
      });
    }
  };

  /**
   * Submit incontinence monitoring to the server
   */
  const submitIncontinenceMonitoring = async (residentId: string) => {
    const { incontinenceTypes } = incontinenceManagementMethods.getValues();

    // Check if there are any changes
    const hasChanges = incontinenceTypes.some((type, index) => {
      const diaper = diapers[index];
      const existingDayUsage = diaperUsage.find(
        (usage) =>
          usage.diaper_id === diaper.diaper_id && usage.usage === 'Day'
      );
      const existingNightUsage = diaperUsage.find(
        (usage) =>
          usage.diaper_id === diaper.diaper_id && usage.usage === 'Night'
      );

      const dayChanged = type.day !== (existingDayUsage?.usage_amount || 0);
      const nightChanged = type.night !== (existingNightUsage?.usage_amount || 0);

      return dayChanged || nightChanged;
    });

    if (!hasChanges) {
      return; // No changes, skip API calls
    }

    const promises: Promise<any>[] = [];

    // Process each incontinence type
    incontinenceTypes.forEach((incontinenceType, index) => {
      const diaper = diapers[index];

      // Handle Day usage
      const existingDayUsage = diaperUsage.find(
        (usage) =>
          usage.diaper_id === diaper.diaper_id && usage.usage === 'Day'
      );

      if (incontinenceType.day > 0) {
        if (!existingDayUsage) {
          promises.push(
            ResidentService.createResidentDiaper(residentId, {
              carehome_diaper_id: diaper.id,
              usage: 'Day',
              usage_amount: incontinenceType.day,
              is_active: true,
            })
          );
        } else if (existingDayUsage.usage_amount !== incontinenceType.day) {
          promises.push(
            DiaperService.updateDiaperUsageAmount(
              residentId,
              existingDayUsage.id,
              incontinenceType.day
            )
          );
        }
      } else if (existingDayUsage) {
        promises.push(
          ResidentService.deleteResidentDiaper(
            residentId,
            existingDayUsage.id
          )
        );
      }

      // Handle Night usage
      const existingNightUsage = diaperUsage.find(
        (usage) =>
          usage.diaper_id === diaper.diaper_id && usage.usage === 'Night'
      );

      if (incontinenceType.night > 0) {
        if (!existingNightUsage) {
          promises.push(
            ResidentService.createResidentDiaper(residentId, {
              carehome_diaper_id: diaper.id,
              usage: 'Night',
              usage_amount: incontinenceType.night,
              is_active: true,
            })
          );
        } else if (existingNightUsage.usage_amount !== incontinenceType.night) {
          promises.push(
            DiaperService.updateDiaperUsageAmount(
              residentId,
              existingNightUsage.id,
              incontinenceType.night
            )
          );
        }
      } else if (existingNightUsage) {
        promises.push(
          ResidentService.deleteResidentDiaper(
            residentId,
            existingNightUsage.id
          )
        );
      }
    });

    // Execute all requests
    await Promise.all(promises);

    // Refresh resident data after updates
    const refreshedResident = await ResidentService.getResident(residentId);
    if (refreshedResident.diapers) {
      setDiaperUsage(
        refreshedResident.diapers as ICreateResidentDiaperResponse[]
      );
    }
  };

  /**
   * Submit body position detection to the server
   */
  const submitBodyPositionDetection = async (residentId: string) => {
    const { positions } = bedsoresPreventionMethods.getValues();

    // Filter positions that have an explicit non-zero value set by the user
    const changedPositions = positions.filter((pos, index) => {
      const hasValidHours = typeof pos.maxHours === 'number' && pos.maxHours > 0;
      if (!hasValidHours) return false;
      const initialPos = initialPositions[index];
      return (
        !initialPos ||
        pos.name !== initialPos.name ||
        pos.maxHours !== initialPos.maxHours
      );
    });

    // Positions explicitly set to 0 should be deleted if they exist on server
    const positionsToDelete = positions.filter((pos) => pos.maxHours === 0);

    // If no changes detected, return early
    if (changedPositions.length === 0 && positionsToDelete.length === 0) {
      return; // No changes, skip API calls
    }

    const promises: Promise<void>[] = [];

    // Update or create changed positions
    changedPositions.forEach((position) => {
      const existingPosition = reposIntervals.find(
        (interval) => interval.type === position.name
      );
      const hh = String(position.maxHours as number).padStart(2, '0');
      const value = `${hh}:00:00`;

      if (existingPosition) {
        promises.push(
          ResidentService.updateResidentReposInterval(
            residentId,
            existingPosition.id,
            {
              resident_id: residentId,
              type: position.name as ReposIntervalType,
              value,
            }
          )
        );
      } else {
        promises.push(
          ResidentService.createResidentReposInterval(residentId, {
            resident_id: residentId,
            type: position.name as ReposIntervalType,
            value,
          })
        );
      }
    });

    // Delete positions with value '00:00:00'
    positionsToDelete.forEach((position) => {
      const existingPosition = reposIntervals.find(
        (interval) => interval.type === position.name
      );
      if (existingPosition) {
        promises.push(
          ResidentService.deleteResidentReposInterval(
            residentId,
            existingPosition.id
          )
        );
      }
    });

    // Execute all requests
    await Promise.all(promises);

    // Fetch resident data again to update the repositioning intervals
    fetchResidentData(residentId);
  };

  /**
   * Submit bed sores (body parts) sequentially on update
   */
  const submitBedSoresSequential = async (residentId: string) => {
    const { enableReposBedsores, selections, bedsoreOps } = bedsoresPreventionMethods.getValues();
    // If section disabled, delete all existing bed sores
    if (!enableReposBedsores) {
      const reverseMap: Record<string, string> = Object.entries(bodyRegions as any).reduce((acc, [key, val]) => {
        acc[val as string] = key;
        return acc;
      }, {} as Record<string, string>);
      const existingKeys = (currentResidentData?.bed_sores || []).map((item) => reverseMap[item.region] || item.region);
      for (const key of existingKeys) {
        await ResidentService.deleteResidentBedsore(residentId, key);
      }
      return;
    }

    const currentSelections = (selections || {}) as Record<string, { risk: string; note: string }>;

    // Build reverse map: human label -> key
    const reverseMap: Record<string, string> = Object.entries(bodyRegions as any).reduce((acc, [key, val]) => {
      acc[val as string] = key;
      return acc;
    }, {} as Record<string, string>);

    // Existing bed sores from server, keyed by region KEY
    const existingMap = (currentResidentData?.bed_sores || []).reduce<Record<string, { risk: string; note: string | null }>>((acc, item) => {
      const regionKey = reverseMap[item.region] || item.region;
      acc[regionKey] = { risk: item.risk, note: item.note };
      return acc;
    }, {});

    // Use queued operations if present; fallback to diff if empty
    const queue = Array.isArray(bedsoreOps) && bedsoreOps.length > 0 ? bedsoreOps : [];

    if (queue.length > 0) {
      // Normalize to last op per key
      const latestByKey = queue.reduce<Record<string, { action: 'create' | 'update' | 'delete'; key: string; risk?: string; note?: string }>>((acc, op) => {
        // squash create+delete into delete, update+delete into delete, create+update -> update
        const prev = acc[op.key];
        if (!prev) {
          acc[op.key] = op;
          return acc;
        }
        if (op.action === 'delete') {
          acc[op.key] = op;
          return acc;
        }
        if (prev.action === 'delete') {
          // keep delete dominant
          return acc;
        }
        // otherwise last write wins for risk/note
        acc[op.key] = op;
        return acc;
      }, {});

      const ops = Object.values(latestByKey);
      const deleteOps = ops.filter(o => o.action === 'delete');
      const upsertOps = ops.filter(o => o.action !== 'delete');

      // 1) deletes first
      for (const op of deleteOps) {
        await ResidentService.deleteResidentBedsore(residentId, op.key);
      }

      // 2) then creates/updates only for keys that are actually changed vs existing
      for (const op of upsertOps) {
        const bodyRegion = (bodyRegions as any)[op.key] || op.key;
        const prev = existingMap[op.key];
        if (prev && prev.risk === (op.risk || '') && (prev.note || '') === (op.note || '')) {
          continue; // no change, skip
        }
        if (prev) {
          await ResidentService.updateResidentBedsore(residentId, op.key, bodyRegion, op.risk || '', op.note || '');
        } else {
          await ResidentService.createResidentBedsore(residentId, bodyRegion, op.risk || '', op.note || '');
        }
      }
      // clear queue after submit
      bedsoresPreventionMethods.setValue('bedsoreOps', []);
    } else {
      const toCreateOrUpdate: Array<{ key: string; risk: string; note: string | null }> = [];
      const toDelete: string[] = [];

      Object.entries(currentSelections).forEach(([uiKey, val]) => {
        const prev = existingMap[uiKey];
        if (!prev) {
          toCreateOrUpdate.push({ key: uiKey, risk: val.risk, note: val.note || null });
        } else if (prev.risk !== val.risk || (prev.note || '') !== (val.note || '')) {
          toCreateOrUpdate.push({ key: uiKey, risk: val.risk, note: val.note || null });
        }
      });

      Object.keys(existingMap).forEach((regionKey) => {
        if (!Object.prototype.hasOwnProperty.call(currentSelections, regionKey)) {
          toDelete.push(regionKey);
        }
      });

      // Deletes first
      for (const regionKey of toDelete) {
        await ResidentService.deleteResidentBedsore(residentId, regionKey);
      }

      // Then upserts (diff-based) only if values actually changed
      for (const item of toCreateOrUpdate) {
        const prev = existingMap[item.key];
        const bodyRegion = (bodyRegions as any)[item.key] || item.key;
        if (prev && prev.risk === item.risk && (prev.note || '') === (item.note || '')) {
          continue; // unchanged, skip
        }
        if (prev) {
          await ResidentService.updateResidentBedsore(residentId, item.key, bodyRegion, item.risk, item.note || '');
        } else {
          await ResidentService.createResidentBedsore(residentId, bodyRegion, item.risk, item.note || '');
        }
      }
    }
  };

  /**
   * Submit general day intervals to the server
   */
  const submitGeneralDayIntervals = async (residentId: string) => {
    const bpValues = bedsoresPreventionMethods.getValues();
    const selectedInterval = `Alle ${bpValues.dayTime} Stunden`;

    console.log('🚀 Starting submit with timeIntervals:', timeIntervals);
    console.log('🚀 Original intervals:', originalIntervals);
    console.log('🚀 Selected interval value:', selectedInterval);

    // Execute operations sequentially (not in parallel)
    console.log('🚀 Starting sequential operations...');

    // Step 1: Delete removed intervals first
    const deletedIntervals = originalIntervals.filter(original => {
      return original.isExisting &&
             original.dbId &&
             !timeIntervals.some(current => current.dbId === original.dbId);
    });

    console.log('🗑️ Found', deletedIntervals.length, 'intervals to delete');

    for (let i = 0; i < deletedIntervals.length; i++) {
      const deletedInterval = deletedIntervals[i];
      console.log(`🗑️ DELETING interval ${i + 1}/${deletedIntervals.length}:`, deletedInterval.dbId);

      try {
        if (deletedInterval.dbId) {
          await ResidentService.deleteResidentReposInterval(residentId, deletedInterval.dbId);
          console.log(`✅ DELETE completed for interval:`, deletedInterval.dbId);
        } else {
          console.warn('⚠️ Skipping delete - dbId is null for interval:', deletedInterval);
        }
      } catch (error) {
        console.error(`❌ DELETE failed for interval:`, deletedInterval.dbId, error);
        throw error; // Re-throw to stop execution
      }

      // Add shorter delay between requests (except for the last one)
      if (i < deletedIntervals.length - 1) {
        console.log('⏱️ Waiting 500ms before next delete...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Step 2: Create or update intervals
    console.log('🔄 Processing', timeIntervals.length, 'intervals for create/update');

    for (let i = 0; i < timeIntervals.length; i++) {
      const interval = timeIntervals[i];

      // Convert 24:00 to 23:59 for API compatibility (both start and end)
      const startTime = interval.start === '24:00' ? '23:59' : interval.start;
      const endTime = interval.end === '24:00' ? '23:59' : interval.end;

      // Use individual interval value for each interval
      const payload = {
        resident_id: residentId,
        type: 'General Day' as const,
        value: interval.intervalValue || '03:00:00',
        start: `${startTime}:00`,
        end: `${endTime}:00`
      };

      console.log(`🔄 Processing interval ${i + 1}/${timeIntervals.length}:`, interval);
      console.log('🔄 Original start time:', interval.start);
      console.log('🔄 Converted start time:', startTime);
      console.log('🔄 Original end time:', interval.end);
      console.log('🔄 Converted end time:', endTime);
      console.log('🔄 Interval value:', interval.intervalValue);

      try {
        if (interval.isExisting && interval.dbId) {
          console.log('✏️ UPDATING existing interval:', interval.dbId);
          await ResidentService.updateResidentReposInterval(residentId, interval.dbId, payload);
          console.log(`✅ UPDATE completed for interval:`, interval.dbId);
        } else {
          console.log('➕ CREATING new interval');
          await ResidentService.createResidentReposInterval(residentId, payload);
          console.log(`✅ CREATE completed for interval:`, interval.start, '-', interval.end);
        }
      } catch (error) {
        console.error(`❌ Operation failed for interval:`, interval, error);
        throw error; // Re-throw to stop execution
      }

      // Add shorter delay between requests (except for the last one)
      if (i < timeIntervals.length - 1) {
        console.log('⏱️ Waiting 500ms before next operation...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    console.log('✅ All operations completed sequentially');

    // Refresh data after operations
    await fetchReposIntervals(residentId);
  };

  /**
   * Handle final submission - call all submit functions in sequence
   */
  const handleFinalSubmission = async () => {
    try {
      setIsLoading(true);
      const featureValues = featureActivationMethods.getValues();

      if (!id) {
        // Build provision payload for create
        const personal = personalDataMethods.getValues();
        const incontinence = incontinenceManagementMethods.getValues();
        const bedSoresValues = bedsoresPreventionMethods.getValues();

        // Resident payload (with features inline)
        const residentPayload: any = {
          carehome_id: user?.carehome?.id || '',
          station_id: stations.find((s) => s.name === personal.livingArea)?.id || '',
          room: personal.room,
          first_name: personal.firstName,
          middle_name: null,
          last_name: personal.lastName,
          weight: personal.weight ? parseFloat(personal.weight) : null,
          height: personal.height ? parseInt(personal.height) : null,
          gender: generateGenderPayload(personal.gender as string),
          date_of_birth: null,
          state: (currentResidentData?.state || 'Enabled') as ResidentState,
          is_active: true,
          has_catheter: Boolean(incontinence?.has_catheter),
          features: [
            featureValues.incontinenceManagementUrine ? 'Incontinence Management (Urine)' : null,
            featureValues.bedsoresPrevention ? 'Repositioning Management' : null,
            featureValues.safeZone ? 'Secure Zone' : null,
            featureValues.fallDetection ? 'Fall Management' : null,
          ].filter(Boolean),
          // feature_options: controlled by feature activation + bedsores settings
          feature_options: (() => {
            if (!featureValues.bedsoresPrevention) return [];
            const opts: string[] = [];
            if (bedSoresValues.enableReposIntervals) {
              opts.push('Repositioning Intervals Enabled');
            }
            if (bedSoresValues.enableReposPositionIntervals) {
              opts.push('Repositioning Position Intervals Enabled');
            }
            if (bedSoresValues.enableReposBedsores) {
              opts.push('Repositioning Bedsores Enabled');
            }
            return opts;
          })(),
        };

        // Diapers payload
        const diapersPayload = (incontinence.incontinenceTypes || []).flatMap((t, index) => {
          const carehomeDiaper = diapers[index];
          const arr: any[] = [];
          if (t.day > 0) {
            arr.push({
              carehome_diaper_id: carehomeDiaper?.id,
              diaper_id: carehomeDiaper?.diaper_id,
              usage: 'Day',
              usage_amount: t.day,
              is_active: true,
            });
          }
          if (t.night > 0) {
            arr.push({
              carehome_diaper_id: carehomeDiaper?.id,
              diaper_id: carehomeDiaper?.diaper_id,
              usage: 'Night',
              usage_amount: t.night,
              is_active: true,
            });
          }
          return arr;
        });

        // Bed sores payload
        // Build bed sores payload from latest selections to include entries from both views (front/back)
        const bedSoresSelections = bedsoresPreventionMethods.getValues().selections || {};
        const bedSoresPayload = Object.entries(bedSoresSelections).map(([position, val]: any) => ({
          region: (bodyRegions as any)[position] || position,
          risk: val?.risk,
          note: val?.note,
        }));

        // Build provision payload with conditional intervals based on feature toggles
        const provisionPayload: any = {
          resident: residentPayload,
          diapers: diapersPayload,
          bed_sores: bedSoresPayload,
        };

        // Only include intervals if body position detection feature is enabled
        if (featureValues.bedsoresPrevention) {
          // Intervals payload: ensure start/end include seconds (HH:mm:ss)
          const generalDayIntervals = bedSoresValues.enableReposIntervals
            ? timeIntervals.map(iv => {
                const rawStart = iv.start && iv.start.length === 5 ? `${iv.start}:00` : iv.start;
                const rawEnd = iv.end && iv.end.length === 5 ? `${iv.end}:00` : iv.end;
                const start = rawStart === '24:00:00' ? '23:59:00' : rawStart;
                const end = rawEnd === '24:00:00' ? '23:59:00' : rawEnd;
                return {
                  type: 'General Day' as const,
                  value: iv.intervalValue,
                  start,
                  end,
                };
              })
            : [];

          const positionIntervals = bedSoresValues.enableReposPositionIntervals
            ? (bedSoresValues.positions || [])
                .filter((p: any) => typeof p.maxHours === 'number' && p.maxHours > 0)
                .map((p: any) => ({ type: p.name as ReposIntervalType, value: String(p.maxHours).padStart(2, '0') + ':00:00' }))
            : [];

          const intervalsPayload = [...generalDayIntervals, ...positionIntervals];
          if (intervalsPayload.length > 0) {
            provisionPayload.intervals = intervalsPayload;
          }
        } else {
          // Feature disabled: send explicit empty array
          provisionPayload.intervals = [];
        }

        const provisionResult = await ResidentService.provisionResident(provisionPayload as any);
        // Store created resident id from provision response for success navigation
        try {
          const newId = (provisionResult && (provisionResult.id || provisionResult.resident_id || provisionResult.resident?.id)) || null;
          if (newId) setCreatedResidentId(String(newId));
        } catch (_) {
          // ignore parse errors, fallback to success screen without id
        }
      } else {
        // Fallback to legacy update flow for edit mode for now
        const residentId = await submitPersonalData();
        await submitFeatureActivation(residentId);
        if (featureValues.incontinenceManagementUrine) {
          await submitIncontinenceMonitoring(residentId);
        }
        if (featureValues.bedsoresPrevention) {
          await submitBedSoresSequential(residentId);
          await submitBodyPositionDetection(residentId);
          await submitGeneralDayIntervals(residentId);
        }
      }

      // Show success toast only in edit flow. In create flow, we show the congratulations screen instead.
      if (id) {
        const successMessage = t(
          getLanguageKeyByType(
            'residentUpdatedSuccess',
            user?.carehome?.type
          )
        );
        toast({
          description: successMessage,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      // When the success toast is shown (create flow), reveal the success component at current step
      if (!id) {
        setIsResidentCreated(true);
        setCreatedAtStepIndex(tabIndex);
      }

      // Close loader right after showing the toast
      setIsLoading(false);

      // Show success screen at the step where creation occurred
      if (!id && isResidentCreated && createdResidentId) {
        setCreatedAtStepIndex(tabIndex);
      }

    } catch (error) {
      console.error('Final submission failed:', error);
      toast({
        description: t('resident.updateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      // Close loader immediately on error as well
      setIsLoading(false);
    } 
  };

  /**
   * Handles the next button click to proceed to the next tab.
   */
  const handleNext = async () => {
    // If success screen is active, ignore next
    if (tabIndex === -1) return;
    let valid = false;
    let shouldAdvanceTab = true;

    // Determine if this is the final step (submit action)
    const bedsoresPreventionEnabled = featureActivationMethods.watch('bedsoresPrevention');
    const incontinenceEnabled = featureActivationMethods.watch('incontinenceManagementUrine');
    
    // Calculate final tab index based on enabled features
    const finalTabIndex = bedsoresPreventionEnabled ? 3 : (incontinenceEnabled ? 2 : 1);
    const isSubmitAction = tabIndex === finalTabIndex;

    if (tabIndex === 0) {
      // Personal Data - only validation, no service call
      valid = await personalDataMethods.trigger();
      if (valid && isSubmitAction) {
        await handleFinalSubmission();
        return;
      }
    } else if (tabIndex === 1) {
      // Feature Activation - only validation, no service call
      valid = await featureActivationMethods.trigger();
      if (
        valid &&
        isSubmitAction &&
        isNewResident &&
        !hasAtLeastOneFeatureEnabled
      ) {
        return;
      }
      if (valid && isSubmitAction) {
        await handleFinalSubmission();
        return;
      }
    } else if (tabIndex === 2) {
      // Incontinence Management - validation only, no service call
      const { incontinenceTypes } = incontinenceManagementMethods.getValues();

      // Custom validation for incontinence management
      const hasValidValues = incontinenceTypes.some(
        (type) => type.day > 0 || type.night > 0
      );

      if (!hasValidValues) {
        setShowIncontinenceValidation(true);
        return;
      }

      valid = await incontinenceManagementMethods.trigger();
      if (valid && isSubmitAction) {
        await handleFinalSubmission();
        return;
      }
    } else if (tabIndex === 3) {
      // Bedsores Prevention - validation only, no service call
      valid = await bedsoresPreventionMethods.trigger();
      if (valid && isSubmitAction) {
        await handleFinalSubmission();
        return;
      }
    } else if (tabIndex === 4) {
      // Hardware Assignment
      valid = true;
    }

    // Handle tab advancement based on shouldAdvanceTab flag
    if (shouldAdvanceTab && valid) {
      // Helper function to find next enabled tab
      const getNextEnabledTab = (currentTab: number): number => {
        if (currentTab === 0) {
          return 1; // Feature Activation is always enabled
        } else if (currentTab === 1) {
          // After Feature Activation, use the form values directly
          const featureValues = featureActivationMethods.getValues();

          if (featureValues.incontinenceManagementUrine) {
            return 2;
          }
          if (featureValues.bedsoresPrevention) {
            return 3;
          }
          return 1;
        } else if (currentTab === 2) {
          // After Incontinence Management, check Bedsores Prevention
          const featureValues = featureActivationMethods.getValues();
          const { incontinenceTypes } = incontinenceManagementMethods.getValues();
          const hasValidValues = Array.isArray(incontinenceTypes) && incontinenceTypes.some((type) => type.day > 0 || type.night > 0);

          if (featureValues.bedsoresPrevention && hasValidValues) {
            return 3;
          }
          return 2;
        } else if (currentTab === 3) {
          return 3; // Current
        }
        return currentTab; // Fallback
      };

      const nextTab = getNextEnabledTab(tabIndex);
      setTabIndex(nextTab);
      if (!id && isSubmitAction && isResidentCreated && createdResidentId) {
        setCreatedAtStepIndex(tabIndex);
      }
    }
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * Handles the previous button click to go back to the previous tab.
   */
  const handlePrev = () => {
    if (tabIndex === -1) {
      navigate('/residents');
      return;
    }
    const currentStepperIndex = stepperTabIndices.indexOf(tabIndex);
    if (currentStepperIndex > 0) {
      setTabIndex(stepperTabIndices[currentStepperIndex - 1]);
      return;
    }
    setTabIndex((prevIndex: number) => Math.max(prevIndex - 1, 0));
  };

  /**
   * Handles tab change.
   *
   * @param {number} index - The index of the tab to switch to.
   */
  const handleTabChange = async (index: number) => {
    if (index > tabIndex) {
      let valid = false;
      if (tabIndex === 0) {
        valid = await personalDataMethods.trigger();
      } else if (tabIndex === 1) {
        valid = await featureActivationMethods.trigger();
      } else if (tabIndex === 2) {
        valid = await incontinenceManagementMethods.trigger();
      } else if (tabIndex === 3) {
        valid = await bedsoresPreventionMethods.trigger();
      }
      if (valid) setTabIndex(index);
    } else {
      setTabIndex(index);
    }
  };

  const gender = personalDataMethods.watch('gender') as
    | 'male'
    | 'female'
    | 'diverse';

  const stationOptions = stations.map((station) => station.name);

  const featureActivationValues = featureActivationMethods.watch();
  const isFeatureActivationComplete =
    featureActivationValues.incontinenceManagementUrine !== null &&
    featureActivationValues.safeZone !== null &&
    featureActivationValues.bedsoresPrevention !== null &&
    featureActivationValues.fallDetection !== null;

  const hasAtLeastOneFeatureEnabled =
    featureActivationValues.incontinenceManagementUrine === true ||
    featureActivationValues.safeZone === true ||
    featureActivationValues.bedsoresPrevention === true ||
    featureActivationValues.fallDetection === true;

  // Check if urine detection is enabled in the form AND carehome supports it
  const incontinenceManagementEnabled =
    featureActivationValues.incontinenceManagementUrine === true &&
    hasFeaturePermission(
      careHome,
      ResidentFeature.INCONTINENCE_MANAGEMENT_URINE
    );

  // const repositioningManagementEnabled =
  //   (id
  //     ? currentResidentData?.features?.includes(
  //         ResidentFeature.REPOSITIONING_MANAGEMENT
  //       ) || false
  //     : featureActivationMethods.watch('bedsoresPrevention')) &&
  //   hasFeaturePermission(careHome, ResidentFeature.REPOSITIONING_MANAGEMENT);

  const bedsoresPreventionEnabled =
    featureActivationValues.bedsoresPrevention === true;
  const isNewResident = !id;
  const urineDecision = featureActivationValues.incontinenceManagementUrine;
  const repositioningDecision = featureActivationValues.bedsoresPrevention;

  const showIncontinenceStep = isNewResident
    ? urineDecision !== false
    : incontinenceManagementEnabled;

  const showRepositioningStep = isNewResident
    ? repositioningDecision !== false
    : bedsoresPreventionEnabled;

  const incontinenceStepAccessible = isNewResident
    ? urineDecision === true
    : incontinenceManagementEnabled;

  const repositioningStepAccessible = isNewResident
    ? repositioningDecision === true
    : bedsoresPreventionEnabled;

  const stepperTabIndices = useMemo(() => {
    const indices = [0, 1];
    if (showIncontinenceStep) indices.push(2);
    if (showRepositioningStep) indices.push(3);
    return indices;
  }, [showIncontinenceStep, showRepositioningStep]);

  const stepperSteps: StepperStep[] = useMemo(() => {
    const steps: StepperStep[] = [
      { id: 1, title: t('personalData') },
      { id: 2, title: t('featureActivation') },
    ];

    if (showIncontinenceStep) {
      steps.push({
        id: 3,
        title: t('incontinenceManagement'),
        isDisabled: urineDecision !== true,
      });
    }
    if (showRepositioningStep) {
      steps.push({
        id: 4,
        title: t('repositioning'),
        isDisabled: repositioningDecision !== true,
      });
    }
    return steps;
  }, [
    isNewResident,
    showIncontinenceStep,
    showRepositioningStep,
    urineDecision,
    repositioningDecision,
    t,
  ]);

  const stepperCurrentStep = Math.max(0, stepperTabIndices.indexOf(tabIndex));

  const maxFormTabIndex = repositioningStepAccessible
    ? 3
    : incontinenceStepAccessible
      ? 2
      : 1;

  useEffect(() => {
    if (tabIndex === 2 && !incontinenceStepAccessible) {
      setTabIndex(1);
    } else if (tabIndex === 3 && !repositioningStepAccessible) {
      setTabIndex(incontinenceStepAccessible ? 2 : 1);
    }
  }, [incontinenceStepAccessible, repositioningStepAccessible, tabIndex]);

  const isStepClickable = (stepperIndex: number) => {
    const step = stepperSteps[stepperIndex];
    if (step?.isDisabled) return false;

    const targetTab = stepperTabIndices[stepperIndex];
    if (targetTab === undefined) return false;

    const incontinenceEnabled = featureActivationMethods.watch('incontinenceManagementUrine');
    const { incontinenceTypes } = incontinenceManagementMethods.getValues();
    const hasValidIncontinence = Array.isArray(incontinenceTypes) && incontinenceTypes.some((type) => type.day > 0 || type.night > 0);

    if (targetTab <= 1) return true;
    if (targetTab === 2 && incontinenceEnabled) return true;
    if (targetTab === 3 && repositioningStepAccessible) {
      return incontinenceEnabled ? hasValidIncontinence : true;
    }
    return targetTab <= tabIndex;
  };

  const handleStepClick = (stepperIndex: number) => {
    const targetTab = stepperTabIndices[stepperIndex];
    if (targetTab === undefined) return;

    const incontinenceEnabled = featureActivationMethods.watch('incontinenceManagementUrine');
    const { incontinenceTypes } = incontinenceManagementMethods.getValues();
    const hasValidIncontinence = Array.isArray(incontinenceTypes) && incontinenceTypes.some((type) => type.day > 0 || type.night > 0);

    if (
      targetTab <= 1 ||
      targetTab <= tabIndex ||
      (targetTab === 2 && incontinenceEnabled) ||
      (targetTab === 3 && repositioningStepAccessible && (incontinenceEnabled ? hasValidIncontinence : true))
    ) {
      handleTabChange(targetTab);
    }
  };


  const getResidentName = () => {
    return `${personalDataMethods.getValues().firstName} ${personalDataMethods.getValues().lastName}`;
  };

  return (
    <Box data-testid="add-resident-page">
      <FormHeader
        title={
          id
            ? personalDataMethods.getValues().firstName +
              ' ' +
              personalDataMethods.getValues().lastName
            : t(getLanguageKeyByType('newResident', user?.carehome?.type))
        }
        redirectUrl={`/residents/${id ? id : ''}`}
      />
      <Box data-testid="add-resident-form" ref={formRef} px="25px" pt="28px" bg="white" borderRadius="20px">
        {/* Replace Tabs with AleaStepper */}
        {(() => {
          const successVisible = ((createdAtStepIndex !== null && tabIndex === createdAtStepIndex) && !id && isResidentCreated) || (new URLSearchParams(location.search).get('success') === '1') || (tabIndex === -1 && !id && isResidentCreated);
          return (
            <AleaStepper
              steps={stepperSteps}
              currentStep={stepperCurrentStep}
              onStepClick={handleStepClick}
              isStepClickable={successVisible ? () => false : isStepClickable}
            />
          );
        })()}

        {(((createdAtStepIndex !== null && tabIndex === createdAtStepIndex) && !id && isResidentCreated) || (new URLSearchParams(location.search).get('success') === '1') || (tabIndex === -1 && !id && isResidentCreated)) ? (
          <ResidentCreatedSuccessfully
            residentName={`${personalDataMethods.getValues().firstName} ${personalDataMethods.getValues().lastName}`}
            residentId={createdResidentId}
          />
        ) : (
          <>
            {/* Tab Content */}
            {tabIndex === 0 && (
              <FormProvider {...personalDataMethods}>
                <PersonalDataTab
                  stations={stations}
                  stationOptions={stationOptions}
                  onRefreshStations={async () => {
                    await fetchStations();
                  }}
                />
              </FormProvider>
            )}

            {tabIndex === 1 && (
              <FormProvider {...featureActivationMethods}>
                <FeatureActivationTab
                  residentName={getResidentName()}
                  careHome={careHome}
                  isNewResident={!id}
                />
              </FormProvider>
            )}

            {tabIndex === 2 && incontinenceStepAccessible && (
              <FormProvider {...incontinenceManagementMethods}>
                <IncontinenceManagementTab
                  diapers={diapers}
                  residentName={getResidentName()}
                  diaperUsage={diaperUsage}
                  showValidation={showIncontinenceValidation}
                  residentId={id || ''}
                  initialHasCatheter={Boolean(currentResidentData?.has_catheter)}
                />
              </FormProvider>
            )}

            {tabIndex === 3 && repositioningStepAccessible && (
              <FormProvider {...bedsoresPreventionMethods}>
                <BedsoresPreventionTab
                  gender={gender}
                  residentId={id}
                  setInitialPositions={setInitialPositions}
                  timeIntervals={timeIntervals}
                  setTimeIntervals={setTimeIntervals}
                  originalIntervals={originalIntervals}
                  setOriginalIntervals={setOriginalIntervals}
                />
              </FormProvider>
            )}

            <ResidentTabNavigation
              handlePrev={handlePrev}
              handleNext={handleNext}
              tabIndex={tabIndex}
              stepperTabIndices={stepperTabIndices}
              finalTabIndex={maxFormTabIndex + 1}
              steps={stepperSteps}
              isEditMode={!!id}
              isNextDisabled={
                tabIndex === 1 &&
                (!isFeatureActivationComplete ||
                  (isNewResident && !hasAtLeastOneFeatureEnabled))
              }
            />
          </>
        )}
      </Box>

     {isLoading && <AleaLoader />}

      {/* Global leave confirm modal for create flow */}
      <AleaConfirmModal
        isOpen={showLeaveConfirm}
        title={t('cancelResidentCreationPopUpHeader')}
        description={t('cancelResidentCreationPopUpDescription')}
        confirmLabel={t('cancelResidentCreationPopUpBackToForm')}
        cancelLabel={t('cancelResidentCreationPopUpLeaveAnyways')}
        onConfirm={() => {
          setShowLeaveConfirm(false);
          pendingNavigateRef.current = null;
        }}
        onCancel={() => {
          const to = pendingNavigateRef.current;
          setShowLeaveConfirm(false);
          if (to) navigate(to);
        }}
      />
    </Box>
  );
};

export default NewResidentPage;
